// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import type { SchemaMigration } from './types'

/**
 * In-memory registry of all known schema migrations.
 *
 * Migrations are registered by schema name and version. The registry ensures
 * versions are unique per schema and provides sorted access for the runner.
 *
 * Design note: sorting on insert (not on read) keeps getMigrationsForSchema
 * O(1) at the cost of a slightly more expensive register(). Since registration
 * happens at startup and reads happen at runtime, this is the right trade-off.
 */
export class MigrationRegistry {
  private readonly migrations = new Map<string, SchemaMigration[]>()

  /**
   * Register a migration.
   *
   * @throws {Error} If a migration with the same schema name and version
   *   is already registered. Duplicate versions indicate a programming error
   *   that must be caught at startup, not silently overwritten.
   */
  register(migration: SchemaMigration): void {
    const existing = this.migrations.get(migration.schema) ?? []

    if (existing.some(m => m.version === migration.version)) {
      throw new Error(
        `Duplicate migration version ${migration.version} for schema '${migration.schema}'`
      )
    }

    existing.push(migration)
    existing.sort((a, b) => a.version - b.version)
    this.migrations.set(migration.schema, existing)
  }

  /**
   * Get all migrations for a schema, sorted by version ascending.
   * Returns an empty array for schemas with no registered migrations.
   */
  getMigrationsForSchema(name: string): SchemaMigration[] {
    return this.migrations.get(name) ?? []
  }

  /**
   * Get migrations with version strictly greater than `currentVersion`,
   * sorted ascending. These are the migrations the runner must apply next.
   */
  getPendingMigrations(name: string, currentVersion: number): SchemaMigration[] {
    return this.getMigrationsForSchema(name).filter(m => m.version > currentVersion)
  }

  /**
   * Get all registered migrations grouped by schema name.
   * Returns a defensive copy — callers cannot mutate registry state.
   */
  getAllMigrations(): Map<string, SchemaMigration[]> {
    return new Map(this.migrations)
  }

  /**
   * Get all schema names that have at least one registered migration,
   * sorted alphabetically.
   */
  getSchemaNames(): string[] {
    return Array.from(this.migrations.keys()).sort()
  }
}
